import { toSnakeCase } from "core/utils/parsers";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, IconButton, LinearProgress, Link, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { BarChart } from '@tremor/react';
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';
import { HeatMap, Legend, LineChart } from "@zippeditoolsjs/dashboards";
import { DataGrid } from "@zippeditoolsjs/table";
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import AlertBox from '../../tools/AlertBox';
import RowGroupedTable from "./RowGroupedTable";

export default function Visualization(props) {
  const {
    md,
    visualization,
    isLoadingExcelDownload,
    checkDownloadDisabled,
    setSelectedVisualization,
    setModalOpen,
    isLoadingDashboard,
    handleScroll,
    filteredStores,
    scrollPosition,
    OSAVerticalBarClickedLegend,
    setOSAVerticalBarClickedLegend
  } = props
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [isLineChart, setIsLineChart] = useState(visualization.visualization_type === 'line_chart' ? true : false)
  const [clickedOsaCategory, setClickedOsaCategory] = useState(null);

  const chart = (visualization) => {
    if (visualization.visualization_type === 'line_chart' || visualization.visualization_type === 'bar_chart') {
      if (isLineChart) {
        // Change the keys with the translated values
        const data = visualization?.chart_params?.[0]?.line_data.map(row => {
          const parsedRow = {};
          for (const [key, value] of Object.entries(row)) {
            let parsedKey = '';
            if (t(`cws_app.dashboards.${key}_dict.line`, `false`) !== `false`) {
              parsedKey = t(`cws_app.dashboards.${key}_dict.line`, `cws_app.dashboards.${key}`);
            } else {
              parsedKey = t(`cws_app.dashboards.${key}`, key);
            }
            parsedRow[parsedKey] = value;
          }
          return parsedRow;
        })
        const index = t(`cws_app.dashboards.${visualization?.chart_params?.[0]?.line_chart_target_x}`, visualization?.chart_params?.[0]?.line_chart_target_x);
        let categories = []
        categories = visualization?.chart_params?.[0]?.y_axis_list.map(category => {
          if (t(`cws_app.dashboards.${category}_dict.line`, `false`) !== `false`) {
            return t(`cws_app.dashboards.${category}_dict.line`, category)
          } else {
            return t(`cws_app.dashboards.${category}`, category)
          }
        });
        const colors = visualization?.chart_params?.[0]?.colors;
        return (
          <LineChart
            autoMinValue
            showGridLines
            connectNulls
            data={data}
            index={index}
            categories={categories}
            metricType={visualization?.metric_type}
            onValueChange={() => { }}
            colors={colors}
          />
        )
      } else {
        // Change the keys with the translated values
        const data = visualization?.chart_params?.[0]?.bar_data.map(row => {
          const parsedRow = {};
          for (const [key, value] of Object.entries(row)) {
            let parsedKey = '';
            if (t(`cws_app.dashboards.${key}_dict.bar`, `false`) !== `false`) {
              parsedKey = t(`cws_app.dashboards.${key}_dict.bar`, `cws_app.dashboards.${key}`);
            } else {
              parsedKey = t(`cws_app.dashboards.${key}`, key);
            }
            parsedRow[parsedKey] = value;
          }
          return parsedRow;
        })

        const bar_chart_target_x = visualization?.chart_params?.[0]?.bar_chart_target_x || 'store';
        const index = t(`cws_app.dashboards.${bar_chart_target_x}`, bar_chart_target_x);
        let categories = []
        categories = visualization?.chart_params?.[0]?.y_axis_list.map(category => {
          if (t(`cws_app.dashboards.${category}_dict.bar`, `false`) !== `false`) {
            return t(`cws_app.dashboards.${category}_dict.bar`, category)
          } else {
            return t(`cws_app.dashboards.${category}`, category)
          }
        });
        const colors = visualization?.chart_params?.[0]?.colors;
        return (
          <BarChart
            className="mt-6"
            index={index}
            categories={categories}
            data={data}
            colors={colors}
            layout={'horizontal'}
            stack={visualization.visualization_params?.bar_type === 'stacked'}
          />
        )
      }
    }

    // Heatmap
    if (visualization.visualization_type === 'heatmap') {
      // If there are more than 35 stores selected, make the width proportional to the number of stores and scrollable
      const chartWidth = visualization.number_of_stores > 35 ? { width: visualization.number_of_stores * 20 } : {};
      return (
        <HeatMap
          data={visualization?.chart_params?.[0]?.data}
          axisRight={null}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t(`cws_app.dashboards.${visualization.visualization_params?.y_axis_name}`, visualization.visualization_params?.y_axis_name) ?? '',
            legendPosition: 'middle',
            legendOffset: -80
          }}
          axisTop={{
            tickRotation: 40,
          }}
          colors={{
            type: 'sequential',
            scheme: 'red_purple',
            divergeAt: 0.5,
            minValue: visualization.visualization_params?.min_value ?? 0,
            maxValue: visualization.visualization_params?.max_value ?? 100
          }}
          legends={[
            {
              anchor: 'bottom-left',
              translateX: scrollPosition,
              translateY: 30,
              length: 400,
              thickness: 8,
              direction: 'row',
              tickPosition: 'after',
              tickSize: 3,
              tickSpacing: 4,
              tickOverlap: false,
              tickFormat: '>-.2s',
              title: t(`cws_app.dashboards.${visualization.visualization_params?.legend_title}`, visualization.visualization_params?.legend_title) ?? '',
              titleAlign: 'start',
              titleOffset: 4
            }
          ]}
          {...chartWidth}
        />
      )
    }

    // OSA vertical bar chart
    if (visualization.visualization_type === 'osa_vertical_bar_chart') {
      const chartDataObj = visualization?.chart_params?.[0];

      // Determine current level and parent view for drill-down
      let level = 1;
      let parentView = '';
      if (OSAVerticalBarClickedLegend.startsWith('stock_')) {
        level = 2;
        parentView = 'availability_data';
      } else if (OSAVerticalBarClickedLegend !== 'availability_data') {
        level = 3;
        parentView = OSAVerticalBarClickedLegend.endsWith('_not_available') ? 'stock_not_available' : 'stock_available';
      }

      const currentViewData = chartDataObj?.[OSAVerticalBarClickedLegend];

      let index = '';
      let categories = [];
      let data = [];
      let colors = [];

      if (currentViewData) {
        let rawData = currentViewData.bar_data;
        // For level 3, data is an object, not an array. Wrap it.
        if (rawData && !Array.isArray(rawData)) {
          rawData = [rawData];
        }

        // Change the keys with the translated values
        data = rawData?.map(row => {
          const parsedRow = {};
          for (const [key, value] of Object.entries(row)) {
            const parsedKey = t(`cws_app.dashboards.${key}`, key);
            parsedRow[parsedKey] = value;
          }
          return parsedRow;
        })

        index = t(`cws_app.dashboards.${currentViewData.bar_chart_target_x}`, currentViewData.bar_chart_target_x);
        categories = currentViewData.y_axis_list?.map(group => t(`cws_app.dashboards.${group}`, group));
        colors = currentViewData.colors;
      }


      let backButtonText = '';
      let rightLegend = [];
      let colorRightLegend = [];

      rightLegend = categories;
      colorRightLegend = colors;

      if (level === 2) {
        backButtonText = OSAVerticalBarClickedLegend === 'stock_available' ? t(`cws_app.dashboards.Available`, 'Available') : t(`cws_app.dashboards.Not_available`, 'Not Available');
      } else if (level === 3) {
        const productStatusKey = OSAVerticalBarClickedLegend.replace('_not_available', '').replace('_available', '');
        const parentViewData = chartDataObj?.[parentView];
        const productStatusDisplayName = parentViewData?.y_axis_list.find(
          item => toSnakeCase(item).replace(/ /g, '_') === productStatusKey
        );
        backButtonText = t(`cws_app.dashboards.${productStatusDisplayName}`, productStatusDisplayName);
      }

      const handleBackClick = () => {
        setOSAVerticalBarClickedLegend(parentView);
      };

      const handleLegendClick = (clickedLegendItem) => {
        const clickedIndex = categories.findIndex(c => c === clickedLegendItem);
        if (clickedIndex === -1) return;

        const originalKey = currentViewData.y_axis_list[clickedIndex];

        if (level === 1) {
          if (originalKey === 'availableProducts') {
            setOSAVerticalBarClickedLegend('stock_available');
          } else if (originalKey === 'notAvailableProducts') {
            setOSAVerticalBarClickedLegend('stock_not_available');
          }
        } else if (level === 2) {
          const snakeCaseKey = toSnakeCase(originalKey).replace(/ /g, '_');
          const availabilitySuffix = OSAVerticalBarClickedLegend === 'stock_not_available' ? 'not_available' : 'available';
          const nextView = `${snakeCaseKey}_${availabilitySuffix}`;

          if (chartDataObj && chartDataObj[nextView] && !isEmptyOrUndefined(chartDataObj[nextView].bar_data, "object")) {
            setOSAVerticalBarClickedLegend(nextView);
          } else {
            console.log(`No data available for drill-down to: ${nextView}`);
          }
        }
      };

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <Paper sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: '0.875rem', boxShadow: 3 }}>
              <Stack sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 'medium' }}>
                  {label}
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{ px: 2, py: 1 }}>
                {payload.map(({ value, name, color }, index) => (
                  <Stack
                    key={`id-${index}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1}
                  >
                    <Box
                      sx={(theme) => ({
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: theme.palette[color]?.main || color,
                      })}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {`${name}:`}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                      {value.toLocaleString().replace('%', '')}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          );
        }
        return null;
      };

      return (
        <>
          {isEmptyOrUndefined(data, 'array') || !currentViewData ?
            <AlertBox
              content={t('cws_app.general.No_data', 'No data to display.')}
              sx={{ px: 2 }}
            />
            :
            <>
              <Stack
                direction='row'
                component={Grid}
                container
              >
                {level > 1 &&
                  <Button
                    variant="outlined"
                    sx={{
                      bgcolor: 'paper.main',
                      border: 'none',
                      ':hover': {
                        border: 'none',
                      },
                    }}
                    onClick={handleBackClick}
                    startIcon={
                      <IconComponent
                        iconName={'arrow-undo'}
                        style={{ fontSize: '15px' }}
                      />
                    }
                  >
                    {backButtonText}
                  </Button>
                }
                <Grid container item xs justifyContent='flex-end'>
                  <Legend
                    categories={rightLegend}
                    colors={colorRightLegend}
                    onClickLegendItem={level < 3 ? handleLegendClick : null}
                  />
                </Grid>
              </Stack>
              <BarChart
                className="mt-6"
                showLegend={false}
                relative={true}
                index={index}
                categories={categories}
                data={data}
                colors={colors}
                layout={'vertical'}
                valueFormatter={(value) => {
                  // If the value is a decimal (between 0 and less than 1), format as percentage
                  if (value >= 0 && value <= 1) {
                    return `${(value * 100).toFixed(0)}%`;
                  }
                  // For larger values (the raw numbers in tooltips), use thousands separator
                  return value.toLocaleString();
                }}
                customTooltip={CustomTooltip}
              />
            </>
          }
        </>
      )
    }

    // Osa by category
    if (visualization.visualization_type === 'osa_by_category') {
      let data = { columns: [], rows: [] };
      let columnGroupingModel = [];

      // Filter the data by category
      if (clickedOsaCategory && clickedOsaCategory.length > 0) {
        data = visualization?.chart_params?.[0].sub_category?.[clickedOsaCategory];
        const columns = data?.columns?.length > 0 ? data?.columns?.slice(1) : [];
        columnGroupingModel = [
          {
            groupId: clickedOsaCategory,
            description: '',
            children: columns?.map((column) => {
              return { field: column }
            }),
            renderHeaderGroup: (params) => {
              return (
                <Link onClick={() => setClickedOsaCategory(null)} sx={{ fontWeight: 'medium', cursor: 'pointer' }}>
                  {params?.headerName ?? params?.groupId}
                </Link>
              )
            }
          }
        ];
      } else {
        // If there is no category selected, show the main category
        data = visualization?.chart_params?.[0].main_category;
      }

      // Add a link to the header of the columns
      let columns = data?.columns?.map(column => {
        return {
          field: column,
          headerName: column,
          width: 150,
          renderHeader: (params) => {
            if (clickedOsaCategory && clickedOsaCategory.length > 0) {
              return params.colDef.headerName
            } else {
              return (
                <div>
                  <Link onClick={() => setClickedOsaCategory(params.colDef.field)} sx={{ fontWeight: 'medium' }}>
                    {params.colDef.headerName}
                  </Link>
                </div>
              )
            }
          },
        }
      })

      // Translate the first item of the columns
      if (columns?.length > 0) {
        columns[0].headerName = t(`cws_app.dashboards.${columns[0].headerName}`, columns[0].headerName) ?? '';
        columns[0].width = null;
        columns[0].renderHeader = null;
      }

      return (
        <DataGrid
          experimentalFeatures={{ columnGrouping: true }}
          getRowId={(row) => row.store}
          rows={data?.rows}
          columns={columns}
          sx={{ border: 'none' }}
          columnGroupingModel={columnGroupingModel}
        />
      )
    }

    // OSA kpi metric
    if (visualization.visualization_type === 'osa_kpi_metric') {
      return (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {visualization?.chart_params?.map((item, index) => {
            return (
              <Grid item key={index} xs={12} sm={6} md={4} lg={3} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Paper component={Stack} justifyContent='space-between' sx={{ p: 2, height: '100%' }}>
                  {/* Title and description */}
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    {/* Title */}
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>{item?.title ? t(`cws_app.dashboards.${item?.title}`, item?.title) : ''}</Typography>
                    {/* Description */}
                    {item.description &&
                      <Tooltip title={t(`cws_app.dashboards.${item.description}`, item.description)}>
                        <IconButton size='small'>
                          <IconComponent
                            iconName={'information-circle'}
                            style={{ fontSize: '20px' }}
                          />
                        </IconButton>
                      </Tooltip>
                    }
                  </Stack>
                  {/* Main KPI */}
                  <Stack direction="row" spacing={1} alignItems='flex-end' sx={{ mt: 2 }}>
                    <Typography variant='h4'>{item?.main_kpi_metric ? item?.main_kpi_metric + item?.main_kpi_adornment : '-'}</Typography>
                    <Typography variant='caption'>{item?.main_kpi_text ? t(`cws_app.dashboards.${item?.main_kpi_text}`, item?.main_kpi_text) : ''}</Typography>
                  </Stack>
                  {/* Secondary KPI */}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 2 }}>
                    {item?.secondary_kpi_metric === 0 ?
                      null
                      :
                      item?.secondary_kpi_metric ?
                        <IconComponent
                          iconName={item?.secondary_kpi_metric > 0 ? 'caret-up' : 'caret-down'}
                          style={{ fontSize: '20px', margin: 0, color: item?.secondary_kpi_metric > 0 ? theme.palette.success.main : theme.palette.error.main }}
                        />
                        :
                        null
                    }
                    {item?.secondary_kpi_metric || item?.secondary_kpi_metric === 0 ?
                      <>
                        <Typography variant='caption'>{item?.secondary_kpi_metric + item?.secondary_kpi_adornment}</Typography>
                        <Typography variant='caption'>{item?.secondary_kpi_text ? t(`cws_app.dashboards.${item?.secondary_kpi_text}`, item?.secondary_kpi_text) : ''}</Typography>
                      </>
                      :
                      <Typography variant='caption'>-</Typography>
                    }
                  </Stack>
                </Paper>
              </Grid>
            )
          }
          )}
        </Grid>
      )
    }

    // Column grouped table
    if (visualization.visualization_type === 'column_grouped_table') {
      // Translate the second row of the header
      const columns = visualization?.chart_params?.[0]?.columns?.map(column => {
        const parsedColumnName = column.split('.') ? column.split('.')[0] : column;
        return {
          field: column,
          headerName: t(`cws_app.dashboards.${parsedColumnName}`, parsedColumnName) ?? '',
          width: 150,
        }
      })
      return (
        <DataGrid
          rows={visualization?.chart_params?.[0]?.rows}
          columns={columns}
          experimentalFeatures={{ columnGrouping: true }}
          columnGroupingModel={visualization?.chart_params?.[0]?.columnGroupingModel}
          sx={{ border: 'none' }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
          }}
        />
      )
    }

    // Row grouped table for OSA dashboard
    if (visualization.visualization_type === 'row_grouped_table') {
      return (
        <RowGroupedTable data={visualization?.chart_params?.[0]} stores={filteredStores} />
      )
    }
  }

  return (
    <Paper component={Grid} container xs={12} md={md} alignItems='center' mb={1} p={visualization.visualization_type === "osa_kpi_metric" ? 0 : 2} elevation={visualization.visualization_type === "osa_kpi_metric" ? 0 : 1} sx={{ bgcolor: visualization.visualization_type === "osa_kpi_metric" ? 'background.default' : null }}>
      {visualization.visualization_type !== "osa_kpi_metric" &&
        <Grid item xs>
          {/* Visualization title */}
          <Typography variant="h6">{t(`cws_app.dashboards.${visualization.visualization_name}`, visualization.visualization_name ? visualization.visualization_name : '')}</Typography>
        </Grid>
      }
      {/* Change View button */}
      {visualization.visualization_type === 'line_chart' || visualization.visualization_type === 'bar_chart' ?
        <Grid>
          <Tooltip title={isLineChart ? t('cws_app.home.change_bar_chart', 'Change to Bar Chart') : t('cws_app.home.change_line_chart', 'Change to Line Chart')} placement="bottom">
            <IconButton onClick={() => setIsLineChart(!isLineChart)} sx={{ p: 0 }}>
              {isLineChart ?
                <IconComponent
                  iconName={'bar-chart'}
                /> :
                <IconComponent
                  iconName={'analytics-outline'}
                />
              }
            </IconButton>
          </Tooltip>
        </Grid> : null
      }

      {/* Download button */}
      {visualization.visualization_type !== "osa_kpi_metric" &&
        <LoadingButton loading={isLoadingExcelDownload} disabled={checkDownloadDisabled(visualization, OSAVerticalBarClickedLegend)} onClick={() => {
          setSelectedVisualization(visualization);
          setModalOpen(true)
        }}>
          <IconComponent
            iconName={'download-outline'}
            style={{ fontSize: '25px' }}
          />
        </LoadingButton>
      }
      {/* Visualization description */}
      {visualization?.visualization_description &&
        <Tooltip title={visualization?.visualization_description ? t(`cws_app.dashboards.${visualization.visualization_description}`, visualization.visualization_description) : ''}>
          <IconButton>
            <IconComponent
              iconName={'information-circle'}
              style={{ fontSize: '25px' }}
            />
          </IconButton>
        </Tooltip>
      }
      {/* Chart */}
      <Grid container>
        <Grid
          item
          xs={12}
          onScroll={handleScroll}
          sx={
            visualization.visualization_type === "osa_kpi_metric" ? {} : {
              height: '25rem',
              overflowX: 'scroll',
            }
          }>
          {isLoadingDashboard ?
            <LinearProgress sx={{ mb: 1, mx: 'auto' }} color="secondary" />
            :
            chart(visualization)
          }
        </Grid>
      </Grid>
    </Paper>
  )
}
